// components/MyPage/UserSummary.jsx
import {
  Card, Box, Typography, Grid,
} from '@mui/material';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import FavoriteIcon from '@mui/icons-material/Favorite';

import CakeIcon from '@mui/icons-material/Cake';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WcIcon from '@mui/icons-material/Wc';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CategoryIcon from '@mui/icons-material/Category';

import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import FlagIcon from '@mui/icons-material/Flag';
import GroupsIcon from '@mui/icons-material/Groups';

import UserNicknameWithEmoticon from '../Common/UserNicknameWithEmoticon';
import { useUser } from '../../contexts/UserContext';
import dayjs from 'dayjs';

/* ───────── 공통 항목 렌더러 ───────── */
const InfoItem = ({ icon, label, value }) => (
  <Typography
    variant="body2"
    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8, whiteSpace: 'nowrap' }}
  >
    {icon} {label}:&nbsp;
    <Box component="span" sx={{ fontWeight: 600 }}>{value}</Box>
  </Typography>
);

export default function UserSummary({ userInfo: external }) {
  const { userInfo: ctx } = useUser?.() || {};
  const userInfo = external || ctx;      // props > Context
  if (!userInfo) return null;

  const {
    username,
    points = 0,
    like_total = 0,
    date_joined,
    joinedAt,
    main_title,
    sub_title,
    equipped_emoticon,
    equipped_emoticon_text,
    gender,
    tendency,
    region_code,
    detailed_traits,
    recruit_main,
    recruit_sub,
    recruit_note,
    recruit_flag,
    offline_status,
    age,
  } = userInfo;

  /* ───── 구인 상태 텍스트 조립 ───── */
  const recruitStatus = recruit_main?.trim() || '미입력';
  let recruitDetail = '미입력';
  if (recruit_main?.trim() === '구인중') {
    recruitDetail = recruit_sub?.trim() || '미입력';
  } else if (recruit_main?.trim() === '구인안함') {
    if (['플파', '디엣', '연디'].includes(recruit_sub?.trim())) {
      recruitDetail = `${recruit_sub}${recruit_note?.trim() ? `(${recruit_note})` : ''}`;
    } else {
      recruitDetail = recruit_sub?.trim() || '미입력';
    }
  }

  /* ───── 3 열에 넣을 데이터 ───── */
  const col1 = [
    { icon: <CalendarTodayIcon fontSize="small" />, label: '가입일', value: dayjs(joinedAt || date_joined).format('YYYY-MM-DD') },
    { icon: <MonetizationOnIcon fontSize="small" />, label: 'Leaf', value: `${points.toLocaleString()} 🌿` },
    { icon: <FavoriteIcon fontSize="small" />, label: '받은 좋아요', value: `${like_total} 개` },
  ];

  const detailedStr = Array.isArray(detailed_traits) && detailed_traits.length > 0
    ? detailed_traits.join(', ')
    : '미입력';

  const col2 = [
    { icon: <CakeIcon fontSize="small" />, label: '나이', value: age ?? '미입력' },
    { icon: <LocationOnIcon fontSize="small" />, label: '지역번호', value: region_code?.trim() || '미입력' },
    { icon: <WcIcon fontSize="small" />, label: '성별', value: gender?.trim() || '미입력' },
    { icon: <PsychologyIcon fontSize="small" />, label: '메인성향', value: tendency?.trim() || '미입력' },
    { icon: <CategoryIcon fontSize="small" />, label: '세부성향', value: detailedStr },
  ];

  const col3 = [
    { icon: <WorkOutlineIcon fontSize="small" />, label: '구인상태', value: recruitStatus },
    { icon: <AssignmentIndIcon fontSize="small" />, label: '상세상태', value: recruitDetail },
    { icon: <FlagIcon fontSize="small" />, label: '관계플래그', value: recruit_flag?.trim() || '미입력' },
    { icon: <GroupsIcon fontSize="small" />, label: '오프여부', value: offline_status?.trim() || '미입력' },
  ];

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      {/* 닉네임 + 칭호 + 이모티콘 */}
      <UserNicknameWithEmoticon
        username={username ?? '익명'}
        mainTitle={main_title}
        subTitle={sub_title}
        emoticonUrl={equipped_emoticon}
        emojiText={equipped_emoticon_text}
        size={30}
      />

      {/* 세로 3-열 레이아웃 */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={4}>
          {col1.map((i) => <InfoItem key={i.label} {...i} />)}
        </Grid>

        <Grid item xs={12} sm={4}>
          {col2.map((i) => <InfoItem key={i.label} {...i} />)}
        </Grid>

        <Grid item xs={12} sm={4}>
          {col3.map((i) => <InfoItem key={i.label} {...i} />)}
        </Grid>
      </Grid>
    </Card>
  );
}
